/**
 * Sales Lead Model
 * 영업 리드 도메인 모델
 */

import { ServiceType } from './AdminUser';

export type SalesStatus = 'new_inquiry' | 'consulting' | 'quote_sent' | 'negotiating' | 'contracted' | 'lost';
export type ActivityType = 'call' | 'email' | 'visit' | 'demo' | 'quote' | 'contract' | 'note' | 'status_change';

export interface SalesLead {
  id: string;
  company_name: string;
  contact_name: string;
  contact_email?: string;
  contact_phone: string;
  address?: string;
  service_type: ServiceType;
  status: SalesStatus;
  contact_id?: string;
  organization_id?: string;
  assigned_to?: string;
  assigned_to_name?: string;
  expected_contract_date?: string;
  contract_amount?: number;
  notes?: string;
  lost_reason?: string;
  created_at?: string;
  updated_at?: string;
  contracted_at?: string;
}

export interface SalesActivity {
  id: string;
  lead_id: string;
  activity_type: ActivityType;
  content: string;
  created_by: string;
  created_by_name: string;
  created_at?: string;
}

// Request Types
export interface CreateLeadRequest {
  company_name: string;
  contact_name: string;
  contact_phone: string;
  service_type: string;
  contact_email?: string;
  address?: string;
  expected_contract_date?: string;
  contract_amount?: number;
  notes?: string;
  contact_id?: string;
}

export interface UpdateLeadRequest {
  company_name?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  expected_contract_date?: string;
  contract_amount?: number;
  notes?: string;
}

export interface UpdateStatusRequest {
  status: SalesStatus;
  reason?: string;
}

export interface AssignLeadRequest {
  assignee_id: string;
  assignee_name: string;
}

export interface CompleteContractRequest {
  organization_id: string;
  contract_amount?: number;
}

export interface AddActivityRequest {
  activity_type: ActivityType;
  content: string;
}

// Helper Functions
export const getSalesStatusText = (status: SalesStatus): string => {
  const statusTexts: Record<SalesStatus, string> = {
    new_inquiry: '신규문의',
    consulting: '상담중',
    quote_sent: '견적발송',
    negotiating: '계약협의',
    contracted: '계약완료',
    lost: '영업실패',
  };
  return statusTexts[status];
};

export const getSalesStatusColor = (status: SalesStatus): string => {
  const statusColors: Record<SalesStatus, string> = {
    new_inquiry: 'purple',
    consulting: 'blue',
    quote_sent: 'yellow',
    negotiating: 'orange',
    contracted: 'green',
    lost: 'gray',
  };
  return statusColors[status];
};

export const getActivityTypeText = (type: ActivityType): string => {
  const typeTexts: Record<ActivityType, string> = {
    call: '전화 상담',
    email: '이메일',
    visit: '방문 상담',
    demo: '데모/시연',
    quote: '견적서 발송',
    contract: '계약 관련',
    note: '메모',
    status_change: '상태 변경',
  };
  return typeTexts[type];
};

export const getActivityTypeIcon = (type: ActivityType): string => {
  const typeIcons: Record<ActivityType, string> = {
    call: '📞',
    email: '📧',
    visit: '📍',
    demo: '🖥️',
    quote: '📄',
    contract: '📝',
    note: '📌',
    status_change: '🔄',
  };
  return typeIcons[type];
};

export const isLeadConsulting = (lead: SalesLead): boolean => {
  return lead.status === 'consulting';
};

export const isLeadContracted = (lead: SalesLead): boolean => {
  return lead.status === 'contracted';
};

export const isLeadLost = (lead: SalesLead): boolean => {
  return lead.status === 'lost';
};

export const isLeadActive = (lead: SalesLead): boolean => {
  return ['new_inquiry', 'consulting', 'quote_sent', 'negotiating'].includes(lead.status);
};

export const isLeadNewInquiry = (lead: SalesLead): boolean => {
  return lead.status === 'new_inquiry';
};

export const isLeadFinal = (lead: SalesLead): boolean => {
  return ['contracted', 'lost'].includes(lead.status);
};

export const canChangeStatus = (lead: SalesLead, newStatus: SalesStatus): boolean => {
  const transitions: Record<SalesStatus, SalesStatus[]> = {
    new_inquiry: ['consulting', 'lost'],
    consulting: ['quote_sent', 'negotiating', 'contracted', 'lost'],
    quote_sent: ['consulting', 'negotiating', 'contracted', 'lost'],
    negotiating: ['consulting', 'quote_sent', 'contracted', 'lost'],
    contracted: [],
    lost: ['consulting'],
  };
  return transitions[lead.status]?.includes(newStatus) ?? false;
};

// Statistics interface
export interface SalesStatistics {
  total: number;
  new_inquiry: number;
  consulting: number;
  quote_sent: number;
  negotiating: number;
  contracted: number;
  lost: number;
  active: number;
  total_contract_amount: number;
}

// 날짜 포맷팅
export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
    maximumFractionDigits: 0,
  }).format(amount);
};
